import React from 'react';
import { Plus, Zap, Info } from 'lucide-react';

interface RBDSidebarProps {
    addPath: (type: 'Series' | 'Parallel' | 'K-out-of-N') => void;
    systemReliability: number | null;
}

export const RBDSidebar: React.FC<RBDSidebarProps> = ({ addPath, systemReliability }) => {
    return (
        <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-blue-600" />
                    Add Subsystem
                </h2>
                <div className="space-y-3">
                    <button
                        onClick={() => addPath('Series')}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all flex items-center justify-between font-medium group"
                    >
                        <span>Series Configuration</span>
                        <div className="flex gap-1">
                            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        </div>
                    </button>
                    <button
                        onClick={() => addPath('Parallel')}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all flex items-center justify-between font-medium group"
                    >
                        <span>Parallel (Redundancy)</span>
                        <div className="flex flex-col gap-1">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                    </button>
                    <button
                        onClick={() => addPath('K-out-of-N')}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all flex items-center justify-between font-medium group"
                    >
                        <span>K-out-of-N Voting</span>
                        <div className="flex items-center gap-1 font-bold text-purple-600">
                            K / N
                        </div>
                    </button>
                </div>
            </div>

            {systemReliability !== null && (
                <div className="bg-blue-600 rounded-xl p-6 text-white shadow-xl">
                    <h3 className="text-sm font-bold uppercase tracking-widest opacity-80 mb-2">Calculated Reliability</h3>
                    <div className="text-4xl font-black mb-2">{(systemReliability * 100).toFixed(4)}%</div>
                    <p className="text-blue-100 text-xs">
                        System availability under selected configuration.
                        Unreliability: {((1 - systemReliability) * 100).toFixed(4)}%
                    </p>

                    <div className="mt-6 pt-6 border-t border-blue-500">
                        <h4 className="text-sm font-bold mb-2 flex items-center gap-2">
                            <Zap className="w-4 h-4 text-amber-300" />
                            AI Insight
                        </h4>
                        <p className="text-xs text-blue-100">
                            {systemReliability < 0.99 ?
                                "Warning: System reliability is below the target 99% baseline. Consider adding parallel redundancy." :
                                "System meets enterprise reliability targets."
                            }
                        </p>
                    </div>
                </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-amber-800 text-xs">
                <Info className="w-4 h-4 flex-shrink-0" />
                <p>
                    Components in <strong>Series</strong> multiply probabilities.
                    Components in <strong>Parallel</strong> only fail if all fail.
                </p>
            </div>
        </div>
    );
};
